import { FolderSync } from "lucide-react";
import { badgeBase, type StatusVariant, statusClasses } from "@/lib/colors";
import { cn } from "@/lib/utils";
import type {
	ShareAction,
	ShareBlockedByServer,
	ShareDecision,
	ShareEset,
	ShareFault,
	ShareHealthCurrent,
	ShareHealthEvent,
	ShareRole,
	ShareSelfHealMode,
	ShareServiceAccount,
} from "./devices-api";
import { formatCacheAge } from "./devices-helpers";
import { useDeviceShareHealthQuery } from "./use-devices-query";

/**
 * Sekcja „Udział KS” rozwiniętego kafla: stan dostępu stanowiska do udziału
 * KS-AOW na serwerze apteki (`\\SERWER\ks`) i decyzje samonaprawy (agent +
 * backend). Tylko apteki własne raportują — bez raportu sekcja się nie
 * pokazuje.
 */

export const SHARE_ROLE_LABELS: Record<ShareRole, string> = {
	station: "Stanowisko",
	server: "Serwer KS (to urządzenie)",
	mapped_drive: "Stanowisko z dyskiem mapowanym (tylko obserwacja)",
	unknown: "Nie ustalono",
};

export const SHARE_FAULT_LABELS: Record<ShareFault, string> = {
	access_denied: "Odmowa dostępu",
	password_expired: "Hasło konta wygasło",
	share_missing: "Brak udziału na serwerze",
	unreachable: "Serwer nieosiągalny",
	other: "Inny błąd",
};

export const SHARE_ACTION_LABELS: Record<ShareAction, string> = {
	reconnect_share: "ponowne podłączenie udziału",
	refresh_credential: "odświeżenie poświadczenia do serwera",
	unlock_server_account: "odblokowanie konta serwisowego na serwerze",
};

export const SHARE_MODE_LABELS: Record<ShareSelfHealMode, string> = {
	repair: "Naprawa",
	observe: "Obserwacja",
	off: "Wyłączona",
};

/** Powody odmowy napraw backendu (`detail.reason`). */
const REFUSAL_LABELS: Record<string, string> = {
	no_pharmacy: "urządzenie bez apteki",
	not_own_pharmacy: "apteka nie jest własna albo jest wykluczona z grupy",
	mode_observe: "tryb obserwacji",
	mode_off: "samonaprawa wyłączona",
	no_station_report: "brak aktualnego raportu stanowiska",
	no_server: "nie wskazano serwera apteki",
	no_server_report: "serwer apteki nie przysłał raportu w ciągu doby",
	server_ip_unknown: "nieznany adres IP serwera",
	server_offline: "serwer apteki offline",
	target_mismatch: "udział wskazuje inny komputer niż serwer apteki",
	target_not_lan: "adres serwera spoza sieci lokalnej apteki",
	peers_network: "inne stanowiska apteki mają awarię sieci, a serwer nie potwierdza problemu konta",
	server_mode_off: "samonaprawa wyłączona lokalnie na serwerze",
	server_unconfirmed: "tylko jedno stanowisko, a serwer nie potwierdza problemu konta",
	operator_recent: "operator niedawno odblokowywał konto",
	budget_6h: "odblokowanie w ciągu ostatnich 6 godz.",
	budget_7d: "wyczerpany limit tygodniowy (serwer 3, stanowisko 6 w 7 dni)",
	budget_24h: "limit 2 odświeżeń w 24 godz.",
	in_progress: "naprawa już trwa",
	device_reassigned: "urządzenie przeniesiono do innej apteki przed wysyłką",
	device_assignment_busy:
		"urządzenie było w tej chwili edytowane lub obsługiwane — nic nie wysłano",
};

const EVENTS_SHOWN = 10;

export function refusalLabel(reason: string | undefined): string {
	if (!reason) return "powód nieznany";
	return REFUSAL_LABELS[reason] ?? reason;
}

export function faultLabel(fault: ShareFault, osError: number | null): string {
	const label = SHARE_FAULT_LABELS[fault] ?? fault;
	return osError == null ? label : `${label} (kod ${osError})`;
}

/** Decyzja agenta po polsku; `act` zależy od trybu i tego, czy akcja poszła. */
export function decisionLabel(
	decision: ShareDecision,
	action: ShareAction | null,
	actionExecuted: boolean,
	mode: ShareSelfHealMode | null,
): string {
	switch (decision) {
		case "healthy":
			return "Dostęp działa";
		case "disabled":
			return "Samonaprawa wyłączona";
		case "wait":
			return "Czekam, czy usterka minie sama";
		case "skip_network":
			return "Pominięto: sieć/serwer nieosiągalne";
		case "server_side":
			return "Problem konta na serwerze — naprawa po stronie serwera";
		case "act": {
			const what = action ? (SHARE_ACTION_LABELS[action] ?? action) : "naprawę";
			if (mode === "observe") return `Tryb obserwacji: wykonałbym ${what}`;
			return actionExecuted ? `Wykonano: ${what}` : `Zaplanowano: ${what}`;
		}
		case "exhausted":
			return "Budżet napraw wyczerpany — potrzebny człowiek";
		case "report_only":
			return "Tylko raport (bez naprawy)";
		default:
			return decision;
	}
}

/** Znane błędy wyniku automatycznego odblokowania (`detail.result.error`). */
const RESULT_ERROR_LABELS: Record<string, string> = {
	device_offline: "urządzenie offline",
	device_unreachable: "urządzenie nie odebrało komendy",
	share_self_heal_mode_observe: "agent odmówił — lokalnie tryb obserwacji",
	share_self_heal_mode_off: "agent odmówił — samonaprawa wyłączona lokalnie",
	share_self_heal_not_own_pharmacy: "agent odmówił — nie potwierdził apteki własnej",
	share_self_heal_target_not_lan: "agent odmówił — adres serwera spoza sieci lokalnej",
};

function resultSuffix(error: string | null | undefined): string {
	if (!error) return "";
	// Błąd wysyłki z backendu przychodzi jako `Conflict: device_offline`.
	const code = error.replace(/^Conflict: /, "");
	return `: ${RESULT_ERROR_LABELS[code] ?? error}`;
}

/**
 * Stan konta `serwis` z raportu serwera KS. `null` = agent go nie odczytał
 * (starszy agent albo brak uprawnień).
 */
export function serviceAccountStatus(account: ShareServiceAccount | null): {
	variant: StatusVariant;
	text: string;
} {
	if (!account) return { variant: "info", text: "nie odczytano" };
	if (!account.exists) return { variant: "warning", text: "brak konta" };
	const problems = [
		account.passwordExpired ? "hasło wygasło" : null,
		account.locked ? "zablokowane" : null,
		account.disabled ? "wyłączone" : null,
	].filter((p): p is string => p !== null);
	if (problems.length > 0) return { variant: "error", text: problems.join(", ") };
	return { variant: "success", text: "OK" };
}

/** Jedna linia historii: zmiana stanu raportu albo naprawa backendu. */
export function eventLabel(event: ShareHealthEvent): string {
	const detail = event.detail ?? {};
	if (event.decision === "server_repair") {
		if (detail.outcome === "executed") {
			return detail.result?.ok
				? "Odblokowano konto serwisowe na serwerze"
				: `Nieudane odblokowanie konta na serwerze${resultSuffix(detail.result?.error)}`;
		}
		if (detail.outcome === "dispatched") {
			return "Wysłano odblokowanie konta serwisowego na serwerze — brak wyniku";
		}
		if (detail.outcome === "observe") {
			return "Tryb obserwacji: odblokowałbym konto serwisowe na serwerze";
		}
		return `Pominięto odblokowanie konta na serwerze: ${refusalLabel(detail.reason)}`;
	}
	if (event.decision === "repair_request") {
		if (detail.outcome === "executed") {
			return detail.result?.ok
				? "Odświeżono poświadczenie do serwera"
				: `Nieudane odświeżenie poświadczenia${resultSuffix(detail.result?.error)}`;
		}
		if (detail.outcome === "dispatched") {
			return "Wysłano odświeżenie poświadczenia do serwera — brak wyniku";
		}
		return `Odrzucono prośbę o odświeżenie poświadczenia: ${refusalLabel(detail.reason)}`;
	}
	// Raport serwera KS: serwer nie sonduje udziału (agent zawsze wysyła
	// `decision = disabled`), więc liczy się tylko stan konta `serwis`.
	if (event.role === "server") {
		return `Raport serwera KS · konto serwis: ${serviceAccountStatus(event.serviceAccount).text}`;
	}
	const decision = decisionLabel(event.decision, event.action, event.actionExecuted, event.mode);
	return reportEventLabel(event, decision);
}

function reportEventLabel(event: ShareHealthEvent, decision: string): string {
	if (event.ok === true) return `OK — ${decision}`;
	if (event.fault) return `${faultLabel(event.fault, event.osError)} — ${decision}`;
	return decision;
}

export function shareTarget(server: string | null, share: string | null): string | null {
	if (!server) return null;
	return `\\\\${server}\\${share ?? ""}`;
}

export function esetLabel(eset: ShareEset): string {
	const base = `ESET: ${eset.product} ${eset.version}`;
	return eset.managed ? `${base} · zarządzany ESET PROTECT` : base;
}

/** Plakietka stanowiska, gdy serwer jest online, a nie odpowiada mu. */
export const BLOCKED_BY_SERVER_BADGE = "Serwer nie odpowiada";

/**
 * Opis dla człowieka, gdy stanowisko nie dostaje odpowiedzi od serwera, choć
 * serwer jest online. NOD32 nie ma ochrony sieci (zapory ani ochrony przed
 * brute force) — wtedy wskazujemy zaporę Windows i sieć.
 */
export function blockedByServerNote(hint: ShareBlockedByServer): string {
	const server = hint.serverHostname ? `Serwer ${hint.serverHostname}` : "Serwer apteki";
	const eset = hint.serverEset;
	if (eset && !/NOD32/i.test(eset.product)) {
		return (
			`${server} jest online, ale nie odpowiada temu stanowisku. Najczęściej blokuje je ESET ` +
			`na serwerze (${eset.product}): po serii nieudanych logowań wpisuje adres stanowiska na ` +
			"czasową czarną listę i odcina cały ruch, także ping. Sprawdź konto serwis na serwerze, " +
			"a w ESET na serwerze: Ustawienia → Ochrona sieci → tymczasowo zablokowane adresy IP."
		);
	}
	return (
		`${server} jest online, ale nie odpowiada temu stanowisku. Sprawdź zaporę Windows na ` +
		"serwerze i sieć między stanowiskiem a serwerem."
	);
}

/** Plakietka nagłówka: serwer KS — stan konta `serwis`; stanowisko — wynik sondy. */
function stateBadge(current: ShareHealthCurrent): { variant: StatusVariant; text: string } {
	if (current.role === "server") {
		const account = serviceAccountStatus(current.serviceAccount);
		return { variant: account.variant, text: `serwis: ${account.text}` };
	}
	if (current.ok === true) return { variant: "success", text: "OK" };
	if (current.fault) {
		return { variant: "error", text: faultLabel(current.fault, current.osError) };
	}
	if (current.ok === false) return { variant: "error", text: "Usterka" };
	return { variant: "info", text: "Brak świeżej sondy" };
}

/** Serwer KS nie sonduje udziału — opis zamiast decyzji (agent wysyła tam zawsze `disabled`). */
export const SERVER_ROLE_NOTE = "Serwer KS — nie sonduje udziału, raportuje konto serwis";

function formatAt(value: string): string {
	return new Date(value).toLocaleString("pl-PL", {
		day: "2-digit",
		month: "2-digit",
		hour: "2-digit",
		minute: "2-digit",
	});
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
	return (
		<div className="flex gap-2 text-xs">
			<span className="w-16 shrink-0 text-muted-foreground">{label}</span>
			<span className="min-w-0 break-words">{children}</span>
		</div>
	);
}

export function ShareHealthSection({
	uuid,
	tenantId,
	idfirm,
}: {
	uuid: string;
	/** Bieżące przypisanie urządzenia — część klucza cache (po przeniesieniu nowy stan). */
	tenantId: number | null;
	idfirm: number | null;
}) {
	// Montowana dopiero po rozwinięciu kafla, więc `enabled` = true.
	const { data, isError } = useDeviceShareHealthQuery(uuid, tenantId, idfirm, true);
	// Błąd (np. stary backend) albo brak raportu → sekcji nie ma.
	if (isError || !data?.current) return null;
	const current = data.current;
	const isServer = current.role === "server";
	// Starszy backend nie wysyła pola — wtedy zwykła usterka sieci.
	const blocked = isServer ? null : (data.blockedByServer ?? null);
	const badge: { variant: StatusVariant; text: string } = blocked
		? { variant: "error", text: BLOCKED_BY_SERVER_BADGE }
		: stateBadge(current);
	const target = shareTarget(current.server, current.share);
	const events = data.events.slice(0, EVENTS_SHOWN);

	return (
		<section aria-label="Udział KS" className="space-y-1.5 rounded-md border border-border p-2">
			<div className="flex items-center gap-1.5 text-xs font-medium">
				<FolderSync className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
				Udział KS
				<span className={cn(badgeBase, statusClasses[badge.variant], "ml-auto")}>{badge.text}</span>
			</div>
			<Row label="Rola">{SHARE_ROLE_LABELS[current.role] ?? current.role}</Row>
			{isServer ? (
				<>
					<p className="text-xs text-muted-foreground">{SERVER_ROLE_NOTE}</p>
					{current.localIp ? (
						<Row label="IP">
							<span className="font-mono">{current.localIp}</span>
						</Row>
					) : null}
				</>
			) : (
				<>
					{target ? (
						<Row label="Cel">
							<span className="font-mono">{target}</span>
						</Row>
					) : null}
					<Row label="Decyzja">
						{decisionLabel(current.decision, current.action, current.actionExecuted, current.mode)}
					</Row>
					{blocked ? <p className="text-xs">{blockedByServerNote(blocked)}</p> : null}
				</>
			)}
			<Row label="Tryb">{SHARE_MODE_LABELS[current.mode] ?? current.mode}</Row>
			<Row label="Raport">
				<span title={new Date(current.updatedAt).toLocaleString("pl-PL")}>
					{formatCacheAge(current.updatedAt)}
				</span>
			</Row>
			{current.eset ? (
				<p className="text-xs text-muted-foreground">{esetLabel(current.eset)}</p>
			) : null}
			{events.length > 0 ? (
				<div className="space-y-0.5 pt-1">
					<div className="text-xs text-muted-foreground">Ostatnie zdarzenia</div>
					<ul className="space-y-0.5">
						{events.map((event) => (
							<li key={event.eventId} className="flex gap-2 text-xs">
								<span className="shrink-0 tabular-nums text-muted-foreground">
									{formatAt(event.receivedAt)}
								</span>
								<span className="min-w-0 break-words">{eventLabel(event)}</span>
							</li>
						))}
					</ul>
				</div>
			) : null}
		</section>
	);
}
